import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  CheckCircle,
  Clear,
  Description,
} from '@mui/icons-material';
import {
  FaCloud,
  FaDownload,
  FaFileExcel,
  FaImages,
} from 'react-icons/fa';
import { useActivityLog } from '../context/ActivityLogContext';
import API_BASE_URL, { apiCall } from '../config/api';
import { formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import InterfaceHeader from '../components/InterfaceHeader';
import { TOOL_COLORS } from '../constants/colors';

const AVAILABLE_TYPES = ['ROOM', 'ROOMAI', 'FRONT', 'DETAIL', 'ICON', 'ROOMBACK'];
const API_PATH = 'CollectionImageLinks';

const CollectionImageLinks = () => {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const [file, setFile] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(['ROOM']);
  const [allVariants, setAllVariants] = useState(false);
  const [rugsMode, setRugsMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const addLog = (processId, message) => {
    contextAddLog(processId, `[${formatLogTime()}] ${message}`);
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) clearIntervalRef(processIdRef.current);
      }
    };
  }, [clearIntervalRef]);

  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const allTypesSelected = selectedTypes.length === AVAILABLE_TYPES.length;
  // Toggles: click "Select all" to select every type; click it again (when everything is
  // already selected) to clear the selection.
  const selectAllTypes = () => setSelectedTypes(allTypesSelected ? [] : [...AVAILABLE_TYPES]);
  const clearAllTypes = () => setSelectedTypes([]);

  const fetchStatus = async (processId) => {
    try {
      const response = await apiCall(`${API_BASE_URL}/api/${API_PATH}/status/${processId}`);

      if (response.logs && response.logs.length > 0) {
        setLogs(processId, response.logs);
      }
      if (response.progressPercentage !== undefined) {
        setProgress(response.progressPercentage);
        updateActivityProcess(processId, { progress: response.progressPercentage });
      }

      if (response.status === 'completed') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }

        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(processId, response.fileName, response.awsUrl);
        }

        setResults({
          success: true,
          processedItems: response.processedItems || 0,
          fileName: response.fileName,
        });
        completeActivityProcess(processId, 'success', 'Processing completed successfully');
        setSuccess('Processing complete! Excel file with S3 image links has been generated.');
        setLoading(false);
        setProgress(null);
      } else if (response.status === 'error') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(processId);
        }
        setError(`Error: ${response.message}`);
        setLoading(false);
        setProgress(null);
        completeActivityProcess(processId, 'error', response.message);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const downloadFile = async (processId, serverFileName, awsUrl) => {
    try {
      addLog(processId, 'Downloading file...');

      if (awsUrl) {
        const link = document.createElement('a');
        link.href = awsUrl;
        link.download = serverFileName || 'Collection_Image_Links.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        addLog(processId, 'Download complete!');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/${API_PATH}/download/${processId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = serverFileName || 'Collection_Image_Links.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      addLog(processId, 'Download complete!');
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(processId, `Download error: ${err.message}`);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/${API_PATH}/sample`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
      });
      if (!response.ok) throw new Error('Failed to download sample file');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
      });
      link.download = `Collection Image Links Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download sample file.');
    }
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const droppedFile = droppedFiles[0];
    if (droppedFile && droppedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
      setFile(droppedFile);
      setError('');
    } else {
      setError('Please drop an Excel or CSV file only (.xlsx, .xls, .csv)');
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
        setError('Please select an Excel or CSV file only.');
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccess('');
    }
  };

  const handleProcess = async () => {
    if (!file) { setError('Please select a file first.'); return; }
    if (selectedTypes.length === 0) { setError('Select at least one image type.'); return; }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(0);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('types', selectedTypes.join(','));
      formData.append('allVariants', String(allVariants));
      formData.append('rugsMode', String(rugsMode));

      const response = await apiCall(`${API_BASE_URL}/api/${API_PATH}/process`, {
        method: 'POST',
        body: formData,
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;
        addActivityProcess(serverProcessId, 'Processing Collection Image Links', 'Collection Image Links');
        addLog(serverProcessId, 'Starting processing...');
        intervalRef.current = setInterval(() => fetchStatus(serverProcessId), 1000);
        setIntervalRef(serverProcessId, intervalRef.current);
      }
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'An error occurred during processing. Please try again.');
      setLoading(false);
      setProgress(null);
    }
  };

  const resetForm = () => {
    setFile(null);
    setError('');
    setSuccess('');
    setResults(null);
    setProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Collection Image Links" category="link" />
        <InterfaceHeader
          title="COLLECTION IMAGE LINKS"
          color={TOOL_COLORS.LINK.primary}
          category="link"
        />

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Upload & Process
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                Upload an Excel/CSV with a single <strong>Collection</strong> column (e.g. ABT141).
                For each selected image type, the tool finds every S3 image whose filename contains
                the collection and ends with the matching type suffix, and writes one sheet per type.
              </Typography>

              {/* Image Type selector */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Image Types
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" onClick={selectAllTypes} sx={{ textTransform: 'none', color: TOOL_COLORS.LINK.primary }}>
                      {allTypesSelected ? 'Deselect all' : 'Select all'}
                    </Button>
                    <Button size="small" onClick={clearAllTypes} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
                      Clear
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {AVAILABLE_TYPES.map((t) => {
                      const active = selectedTypes.includes(t);
                      return (
                        <Chip
                          key={t}
                          label={t}
                          clickable
                          onClick={() => toggleType(t)}
                          sx={{
                            fontWeight: 600,
                            bgcolor: active ? TOOL_COLORS.LINK.primary : 'transparent',
                            color: active ? '#fff' : theme.palette.text.primary,
                            border: `1px solid ${active ? TOOL_COLORS.LINK.primary : theme.palette.divider}`,
                            '&:hover': {
                              bgcolor: active ? TOOL_COLORS.LINK.hover : TOOL_COLORS.LINK.light,
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allVariants}
                        onChange={(e) => setAllVariants(e.target.checked)}
                        sx={{
                          color: TOOL_COLORS.LINK.primary,
                          '&.Mui-checked': { color: TOOL_COLORS.LINK.primary },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Include all numbered variants (e.g. -DETAIL99).{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          When off, only -TYPE and -TYPE1 through -TYPE10 are included.
                        </Typography>
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rugsMode}
                        onChange={(e) => setRugsMode(e.target.checked)}
                        sx={{
                          color: TOOL_COLORS.LINK.primary,
                          '&.Mui-checked': { color: TOOL_COLORS.LINK.primary },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Rugs &mdash; largest size first.{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          Biggest sizes go on the left: &minus;1518, &minus;1420, then the 12&prime;, 11&prime;, 10&prime; groups,
                          down through SQ / R / OV, runners, and accents.
                        </Typography>
                      </Typography>
                    }
                  />
                </CardContent>
              </Card>

              {/* File Upload Area */}
              <Card sx={{ mb: 3, bgcolor: file ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.LINK.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Excel / CSV File
                    </Typography>
                    {file && (
                      <Tooltip title="1 file selected">
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                  />

                  <Box
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.LINK.primary : file ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragging
                        ? TOOL_COLORS.LINK.light
                        : file
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: file ? '#4CAF50' : TOOL_COLORS.LINK.primary,
                        bgcolor: file
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.LINK.light,
                      }
                    }}
                  >
                    {!file ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.LINK.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragging ? 'Drop file here' : 'Drop your Excel or CSV file here or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Excel (.xlsx, .xls) or CSV (.csv) files supported
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>1 file selected</Typography>
                        <Typography variant="caption" color="text.secondary">Click or drop to replace</Typography>
                      </>
                    )}
                  </Box>

                  {file && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                          borderColor: TOOL_COLORS.LINK.primary,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: TOOL_COLORS.LINK.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({formatFileSize(file.size)})
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleProcess}
                  disabled={!file || loading || selectedTypes.length === 0}
                  sx={{
                    bgcolor: TOOL_COLORS.LINK.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.LINK.hover },
                    px: 4
                  }}
                  startIcon={<FaCloud />}
                >
                  {loading ? 'Processing...' : 'Process'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDownloadSample}
                  disabled={loading}
                  sx={{
                    borderColor: TOOL_COLORS.LINK.primary,
                    color: TOOL_COLORS.LINK.primary,
                    '&:hover': { borderColor: TOOL_COLORS.LINK.hover, bgcolor: TOOL_COLORS.LINK.light }
                  }}
                  startIcon={<FaDownload />}
                >
                  Download Template
                </Button>
                {(file || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Info Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Processing file...</Typography>
                  <LinearProgress
                    variant={progress > 0 ? 'determinate' : 'indeterminate'}
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                      '& .MuiLinearProgress-bar': { backgroundColor: TOOL_COLORS.LINK.primary, borderRadius: 4 }
                    }}
                  />
                  {progress > 0 && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                      {Math.round(progress)}% complete
                    </Typography>
                  )}
                </Box>
              )}

              {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Processing Results:</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><FaImages color={TOOL_COLORS.LINK.primary} /></ListItemIcon>
                      <ListItemText
                        primary={`${results.processedItems || 0} Collection(s) processed`}
                        secondary={`${selectedTypes.length} image type(s) searched`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><FaFileExcel color="#4CAF50" /></ListItemIcon>
                      <ListItemText
                        primary="Excel file generated"
                        secondary="One sheet per selected type"
                      />
                    </ListItem>
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Excel file has been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Instructions:</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Download the template file for reference<br />
                2. Fill in collections in column A (e.g. ABT141, MAD604)<br />
                3. Select one or more image types above<br />
                4. Upload your file using drag & drop or browse<br />
                5. Click "Process" to start<br />
                6. Results Excel with S3 links will auto-download
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Matching rules:</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, fontSize: 13 }}>
                &bull; <strong>Collection</strong>: filename must <em>start with</em> the collection code (case-insensitive). e.g. <code>AT24</code> won&apos;t match <code>PAT24...</code>.
                <br />
                &bull; <strong>Suffix (default)</strong>: filename ends with <code>-TYPE</code>, <code>-TYPE1</code>, … <code>-TYPE10</code>.
                <br />
                &bull; <strong>Suffix (all variants)</strong>: filename ends with <code>-TYPE</code> followed by any digits.
                <br />
                &bull; <strong>ROOM / ROOMAI / ROOMBACK</strong>: images containing <code>COMPOSITE</code> or <code>DOG</code> are placed at the end of the row.
                <br />
                &bull; <strong>Rugs mode</strong>: within each collection, sort by canonical size (biggest first): 1518, 1420, then the 12&prime; / 11&prime; / 10&prime; ... groups, then SQ / R / OV, then runners, then accents.
                <br />
                &bull; One worksheet per selected type, named after the type.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CollectionImageLinks;
